import { Component, OnInit, AfterViewInit, ViewChild, Output, OnChanges, Input, EventEmitter, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { PartnerService } from './services/partner.service';
import { PartnerSearchSettings } from './models/partner-search-settings';
import { Partner } from './models/partner';
import { PartnerSearch } from './models/partner-search';
import { DxLookupComponent, DxLookupModule } from 'devextreme-angular';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { CurrentUserService } from '../user/services';
import { ConfigurationService } from '../configuration/services/configuration.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DxoDropDownOptionsModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';


@Component({
    templateUrl: './partner-selector.component.html',
    selector: 'ep-partner-selector',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PartnerSelectorComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxLookupModule, DxoDropDownOptionsModule, DxTemplateModule]
})

export class PartnerSelectorComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor  {

    constructor(
        private partnerService: PartnerService,
        private currentUserService: CurrentUserService,
        private configService: ConfigurationService   ) {
    }

    public searchSettings: PartnerSearchSettings = new PartnerSearchSettings();
    public partnerDataSource: any;
    public minSearchLength = 0;
    public loadingPartnerDetails = false;
    public hasPermissionToSearch = true;
    public isSearchResultRestricted = false;
    @Input() filterPartnerType: string = null;
    @Input() isRequired = false;
    @Input() isReadOnly = false;
    @Input() selectedPartner: Partner;
    @Input() showLoadingPartnerDetails = true;
    @Output() selectedPartnerChange: EventEmitter<Partner> = new EventEmitter<Partner>();
    @Output() selectedPartnerIdChange: EventEmitter<number> = new EventEmitter<number>();

    @Input() value: string = null;

    @ViewChild('partnerLookup', { static: true }) private partnerLookup: DxLookupComponent;

    async ngOnInit(): Promise<void> {
        const store = new CustomStore({
            load: (loadOptions: any) => {
                if (loadOptions.searchValue) {
                    this.searchSettings.searchString = loadOptions.searchValue;
                    return this.partnerService.searchPartners(this.searchSettings).then(partnerResult => {
                        this.isSearchResultRestricted = partnerResult.IsPartnerSearchResultRestricted;
                        if (this.filterPartnerType) {
                            partnerResult.Partners = partnerResult.Partners.filter(e => e.PartnerTypeName === this.filterPartnerType);
                        }
                        let partners = partnerResult.Partners.sort((p1, p2) => p1.PartnerName.localeCompare(p2.PartnerName));
                        if (partners.length > 100) {
                            partners = partners.slice(0, 100);
                            const limitTemplatePartner = new PartnerSearch();
                            limitTemplatePartner['moreResultTemplate'] = true;
                            limitTemplatePartner['disabled'] = true;
                            partners.push(limitTemplatePartner);
                        }
                        if (this.isSearchResultRestricted === true){
                            const dataRestrictionPartner = new PartnerSearch();
                            dataRestrictionPartner['restrictionTemplate'] = true;
                            dataRestrictionPartner['disabled'] = true;
                            partners.push(dataRestrictionPartner);
                        }

                        return partners;
                    });
                }
                return undefined;
            },
            byKey: (key: any) => key
        });

        this.partnerDataSource = new DataSource({
            store,
            paginate: false
        });
        const max = +(await this.configService.getEffectiveSettingValue('PartnerSearch_MinimumSearchLength'));
        if (!Number.isNaN(max)) {
            this.minSearchLength = max;
        }


        this.hasPermissionToSearch = this.currentUserService.hasPermission('Partner_View_Search');
    }

    ngAfterViewInit(): void {
        if (this.selectedPartner) {
            // setSelectedPartner can have been called before the AfterViewInit event - and the DevExpress widget is not ready before this event.
            // Therefore set the selected partnere here if it has been set before.
            this.setSelectedPartner(this.selectedPartner);
        }
    }

    ngOnChanges(changes: any): void {
        if (changes.selectedPartner) {
            this.setSelectedPartner(this.selectedPartner);
        }
    }

    reset(): void {
        this.setSelectedPartner(null);
    }

    setSelectedPartnerId(partnerId: number): void {
        this.loadingPartnerDetails = true;
        this.partnerService.getPartner(partnerId).then((partner) => {
            this.setSelectedPartner(partner);
            this.loadingPartnerDetails = false;
            this.selectedPartnerChange.emit(partner);
        });
    }

    setSelectedPartner(partner: Partner): void {
        if (this.partnerLookup.instance) {
            if (partner) {
                this.partnerLookup.isValid = true;
                // Create an instance of the PartnerSearch object so the devexpress template can be re-used (Partner-object != PartnerSearch-object).
                const partnerSearch = new PartnerSearch();
                partnerSearch.PartnerId = partner.PartnersId;
                partnerSearch.EdiPortalId = partner.EdiPortalId;
                partnerSearch.PartnerName = partner.PartnerName;
                // IMPORTANT! This value is set to prevent an infinite loop!
                partnerSearch['ignore'] = true;

                this.partnerLookup.instance.option('value', partnerSearch);
            }
            else {
                if (this.isRequired) {
                    this.partnerLookup.isValid = false;
                }
                this.partnerLookup.instance.option('value', null);

            }

        }
    }

    partnerSelectedInternal(e: any): void {
        if (e && e.value && !e.value.ignore) {
            const partnerSearch = e.value as PartnerSearch;

            // Get the real Partner-object from the backend
            if (this.selectedPartnerChange.observers.length > 0) {
                this.partnerLookup.isValid = true;
                this.loadingPartnerDetails = true;
                this.partnerService.getPartner(partnerSearch.PartnerId).then(partner => {
                    this.selectedPartnerChange.emit(partner);
                    this.selectedPartner = partner;
                    this.loadingPartnerDetails = false;
                });
            }

            this.selectedPartnerIdChange.emit(partnerSearch.PartnerId);
            this.onChanged(partnerSearch.PartnerId);

        }
        else if (e && !e.value) {
            if (this.isRequired) {
                this.partnerLookup.isValid = false;
            }
            this.selectedPartnerChange.emit(null);
            this.selectedPartnerIdChange.emit(null);
            this.onChanged(null);
            this.selectedPartner = null;
        }
    }

    isValid(): boolean {
        return this.partnerLookup.isValid;
    }



    writeValue(value: any): void {
        this.value = value
        if (value) {
            this.setSelectedPartnerId(value);
        }
    }

    onChanged: (value: any) => void = () => { };
    registerOnChange(fn: any): void {
        this.onChanged = fn;
    }

    onTouched = () => { };
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.partnerLookup.disabled = isDisabled;
    }
}
